// Style dùng chung cho Bói Đê: icon + màu accent riêng từng chủ đề (theo slug).
// Class Tailwind phải là literal đầy đủ (JIT không build class động).
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  Baby,
  Briefcase,
  CalendarCheck,
  CalendarHeart,
  Car,
  Coins,
  Compass,
  Gem,
  GraduationCap,
  Hammer,
  Handshake,
  Heart,
  HeartHandshake,
  HeartPulse,
  Home,
  Layers,
  Plane,
  Rocket,
  Scale,
  Sparkles,
  Store,
  TrendingUp,
  UsersRound,
  Zap,
} from "lucide-react";

export function formatVnd(n: number): string {
  return `${n.toLocaleString("vi-VN")}đ`;
}

// Map tên icon (cột `icon` trong DB) → component lucide.
export const TOPIC_ICONS: Record<string, LucideIcon> = {
  ArrowLeftRight,
  Baby,
  Briefcase,
  CalendarCheck,
  CalendarHeart,
  Car,
  Coins,
  Compass,
  Gem,
  GraduationCap,
  Hammer,
  Handshake,
  Heart,
  HeartHandshake,
  HeartPulse,
  Home,
  Layers,
  Plane,
  Rocket,
  Scale,
  Sparkles,
  Store,
  TrendingUp,
  UsersRound,
  Zap,
};

export function topicIcon(iconName: string): LucideIcon {
  return TOPIC_ICONS[iconName] ?? Sparkles;
}

const TOPIC_DISPLAY_NAMES: Record<string, string> = {
  "khi-nao-tinh-duyen": "Bao giờ có tình duyên?",
  "nguoi-ay-moi-quan-he": "Người ấy nghĩ gì về tôi?",
  "hop-tuoi-hon-nhan": "Có hợp để tiến tới hôn nhân?",
  "hop-nhau-tien-toi-hon-nhan": "Có hợp để tiến tới hôn nhân?",
  "thang-tien-phong-van": "Cơ hội thăng tiến",
  "ngay-khai-truong": "Xem ngày khai trương",
  "ngay-khai-truong-ky-hop-dong": "Xem ngày khai trương",
  "ngay-dong-tho": "Xem ngày nhà cửa",
  "ngay-dong-tho-nhap-trach": "Xem ngày nhà cửa",
  "ngay-mua-xe": "Xem ngày nhận xe",
  "ngay-mua-nhan-xe": "Xem ngày nhận xe",
  "ngay-xuat-hanh": "Xem ngày xuất hành",
  "xuat-hanh-di-xa": "Xem ngày xuất hành",
};

/** Tên hướng đến nhu cầu người dùng; slug và dữ liệu API vẫn giữ nguyên. */
export function topicDisplayName(slug: string, fallback: string): string {
  return TOPIC_DISPLAY_NAMES[slug] ?? fallback;
}

export type TopicAccent = {
  /** màu chữ/icon */
  text: string;
  /** nền nhạt cho vòng icon */
  bg: string;
  /** viền khi hover tile */
  border: string;
};

const DEFAULT_ACCENT: TopicAccent = {
  text: "text-[#E5B869]",
  bg: "bg-[#E5B869]/12",
  border: "hover:border-[#E5B869]/60",
};

export const TOPIC_ACCENTS: Record<string, TopicAccent> = {
  "tinh-duyen": {
    text: "text-rose-300",
    bg: "bg-rose-500/15",
    border: "hover:border-rose-400/60",
  },
  "hon-nhan-gia-dao": {
    text: "text-pink-300",
    bg: "bg-pink-500/15",
    border: "hover:border-pink-400/60",
  },
  "con-cai": {
    text: "text-fuchsia-300",
    bg: "bg-fuchsia-500/15",
    border: "hover:border-fuchsia-400/60",
  },
  "hop-tac-lam-an": {
    text: "text-teal-300",
    bg: "bg-teal-500/15",
    border: "hover:border-teal-400/60",
  },
  "su-nghiep": {
    text: "text-violet-300",
    bg: "bg-violet-500/15",
    border: "hover:border-violet-400/60",
  },
  "cong-viec": {
    text: "text-sky-300",
    bg: "bg-sky-500/15",
    border: "hover:border-sky-400/60",
  },
  "tai-loc": {
    text: "text-amber-300",
    bg: "bg-amber-500/15",
    border: "hover:border-amber-400/60",
  },
  "hoc-hanh": {
    text: "text-indigo-300",
    bg: "bg-indigo-500/15",
    border: "hover:border-indigo-400/60",
  },
  "van-han": {
    text: "text-orange-300",
    bg: "bg-orange-500/15",
    border: "hover:border-orange-400/60",
  },
  "suc-khoe": {
    text: "text-emerald-300",
    bg: "bg-emerald-500/15",
    border: "hover:border-emerald-400/60",
  },
  "mua-nha-dat": {
    text: "text-lime-300",
    bg: "bg-lime-500/15",
    border: "hover:border-lime-400/60",
  },
  "xuat-hanh": {
    text: "text-cyan-300",
    bg: "bg-cyan-500/15",
    border: "hover:border-cyan-400/60",
  },
  "xem-ngay": {
    text: "text-yellow-300",
    bg: "bg-yellow-500/15",
    border: "hover:border-yellow-400/60",
  },
  // Thẻ mới v72
  "hoi-nhanh": {
    text: "text-yellow-300",
    bg: "bg-yellow-500/15",
    border: "hover:border-yellow-400/60",
  },
  "so-sanh-lua-chon": {
    text: "text-sky-300",
    bg: "bg-sky-500/15",
    border: "hover:border-sky-400/60",
  },
  "khi-nao-tinh-duyen": {
    text: "text-rose-300",
    bg: "bg-rose-500/15",
    border: "hover:border-rose-400/60",
  },
  "nguoi-ay-moi-quan-he": {
    text: "text-pink-300",
    bg: "bg-pink-500/15",
    border: "hover:border-pink-400/60",
  },
  "hop-tuoi-hon-nhan": {
    text: "text-red-300",
    bg: "bg-red-500/15",
    border: "hover:border-red-400/60",
  },
  "doi-viec-hay-o-lai": {
    text: "text-sky-300",
    bg: "bg-sky-500/15",
    border: "hover:border-sky-400/60",
  },
  "thang-tien-phong-van": {
    text: "text-violet-300",
    bg: "bg-violet-500/15",
    border: "hover:border-violet-400/60",
  },
  "ngay-cuoi-hoi": {
    text: "text-pink-300",
    bg: "bg-pink-500/15",
    border: "hover:border-pink-400/60",
  },
  "ngay-khai-truong": {
    text: "text-amber-300",
    bg: "bg-amber-500/15",
    border: "hover:border-amber-400/60",
  },
  "ngay-dong-tho": {
    text: "text-orange-300",
    bg: "bg-orange-500/15",
    border: "hover:border-orange-400/60",
  },
  "ngay-mua-xe": {
    text: "text-cyan-300",
    bg: "bg-cyan-500/15",
    border: "hover:border-cyan-400/60",
  },
  "ngay-xuat-hanh": {
    text: "text-lime-300",
    bg: "bg-lime-500/15",
    border: "hover:border-lime-400/60",
  },
};

export function topicAccent(slug: string): TopicAccent {
  return TOPIC_ACCENTS[slug] ?? DEFAULT_ACCENT;
}

// Nhóm hiển thị trên hub theo cột DB group_key (v72) — label + thứ tự ở FE,
// topic thuộc nhóm nào do DB quyết định; group_key lạ rơi vào "Chủ đề khác".
export const GROUP_META: Array<{ key: string; label: string }> = [
  { key: "xem-nhanh", label: "Xem nhanh" },
  { key: "tinh-cam", label: "Tình cảm & Nhân duyên" },
  { key: "cong-danh", label: "Công danh & Tài chính" },
  { key: "gia-dinh", label: "Gia đình" },
  { key: "nha-cua", label: "Nhà cửa & Đời sống" },
  { key: "kham-pha", label: "Khám phá bản thân" },
];

// Back-compat: nhóm cũ theo slug — dùng khi BE chưa migrate v72 (mọi topic
// group_key='khac' → hub rơi về cách nhóm này).
export const TOPIC_GROUPS: Array<{ key: string; label: string; slugs: string[] }> = [
  {
    key: "tinh-cam",
    label: "Tình cảm & Nhân duyên",
    slugs: ["tinh-duyen", "hon-nhan-gia-dao", "con-cai"],
  },
  {
    key: "su-nghiep-tien-tai",
    label: "Sự nghiệp & Tiền tài",
    slugs: ["su-nghiep", "cong-viec", "tai-loc", "hop-tac-lam-an", "hoc-hanh"],
  },
  {
    key: "van-trinh",
    label: "Vận trình & Đời sống",
    slugs: ["van-han", "suc-khoe", "mua-nha-dat", "xuat-hanh"],
  },
  {
    key: "ngay-lanh",
    label: "Chọn ngày lành",
    slugs: ["xem-ngay"],
  },
];
